import { Box, Button } from "@modernatx-internal/component-library";
import React from "react";

import { useExperience } from "@/context/ExperienceContext";
import { useFinder } from "@/finder/hooks/V2/useFinder";
import { customerWebsite } from "@/finder/utils";
import type { FinderLocation } from "@/types/FinderLocation";
import type { Language } from "@/types/Language";

import type { ScheduleModalProps } from "./ScheduleModal";
import { ScheduleModal } from "./ScheduleModal";

const hostnameTemplate = "{hostname}";

const getDistanceInMilesAndKm = (distanceInMeters: number) => {
  const distanceInMiles = distanceInMeters * 0.000621371;
  const distanceInKilometers = distanceInMeters / 1000;
  return { distanceInMiles, distanceInKilometers };
};

const getHostname = (href?: string) => {
  if (!href) {
    return;
  }
  try {
    return new URL(href || "").hostname.replace("www.", "");
  } catch (e) {
    return;
  }
};

const formatHostname = (hostname: string) => {
  if (hostname.toLowerCase() === "cvs.com") {
    return hostname.slice(0, 3).toUpperCase() + hostname.slice(3);
  } else if (hostname.toLowerCase() === "riteaid.com") {
    return "RiteAid.com";
  }
  return hostname.charAt(0).toUpperCase() + hostname.slice(1);
};

const getFormattedDisplayTexts = ({
  location,
  language,
  scheduleNowTextTemplate,
  scheduleModalProps
}: {
  location: FinderLocation;
  language?: Language;
  scheduleNowTextTemplate: string;
  scheduleModalProps: ScheduleModalProps;
}) => {
  const href = customerWebsite({ customer: location.customer, language });

  const hostname = getHostname(href);
  if (!hostname || !href) {
    return null;
  }
  const formattedHostname = formatHostname(hostname);

  const scheduleNowText = scheduleNowTextTemplate.replace(
    hostnameTemplate,
    formattedHostname || ""
  );
  const title = scheduleModalProps.title.replace(hostnameTemplate, formattedHostname || "");
  const buttonText = scheduleModalProps.buttonText.replace(
    hostnameTemplate,
    formattedHostname || ""
  );
  return { scheduleNowText, scheduleModal: { href, buttonText, title } };
};

export const ScheduleNowButton = ({ location }: { location: FinderLocation }) => {
  const { scheduleNowTextTemplate, scheduleModalProps, isMobile } = useFinder();
  const { language } = useExperience();

  const [isScheduleModalOpen, setIsScheduleModalOpen] = React.useState(false);
  const openModal = () => setIsScheduleModalOpen(true);
  const closeModal = () => setIsScheduleModalOpen(false);

  const { distanceInMiles, distanceInKilometers } = getDistanceInMilesAndKm(
    location.location.distance
  );
  const formattedDisplayTexts = getFormattedDisplayTexts({
    location,
    language,
    scheduleNowTextTemplate,
    scheduleModalProps
  });

  if (!formattedDisplayTexts) {
    return null;
  }

  return (
    <Box>
      <Button
        sx={{ verticalAlign: "top" }}
        variant={isMobile ? "filled" : "outline"}
        data-track="schedule"
        data-track-miles={distanceInMiles}
        data-track-kilometers={distanceInKilometers}
        icon="arrow-right"
        iconPosition="end"
        onClick={openModal}
      >
        {formattedDisplayTexts.scheduleNowText}
      </Button>
      <ScheduleModal
        buttonText={formattedDisplayTexts.scheduleModal.buttonText}
        closeModal={closeModal}
        exitLink={formattedDisplayTexts.scheduleModal.href}
        isOpen={isScheduleModalOpen}
        subBlockText={scheduleModalProps.subBlockText}
        mainBlockText={scheduleModalProps.mainBlockText}
        title={formattedDisplayTexts.scheduleModal.title}
        locationName={location.customer.name}
      />
    </Box>
  );
};
